import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    TooltipActionColorsConstantType,
    TooltipInverseThemeConstantType,
    TooltipIconColorConstantType,
    TooltipPlacementConstantType,
    TooltipLocationConstantType,
    TooltipCaretConstantType,
    TooltipActionColorsType,
    TooltipDynamicStyleType,
    TooltipCustomThemeType,
    TooltipPlacementType,
    TooltipLocation,
    TooltipMode,
    TooltipSide
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle,
    STATE_COLOR_KEYS
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const TOOLTIP_TABBABLE_SELECTOR = "a[href], button, input, select, textarea, iframe, summary, [tabindex], [contenteditable]";

export const TOOLTIP_EDITABLE_SELECTOR = "input, textarea, select, [contenteditable]";

export const TOOLTIP_INTERACTIVE_PROPS: Array<string> = [
    "onAccessibilityAction",
    "onAccessibilityTap",
    "onSelectionChange",
    "onSlidingComplete",
    "onCheckedChange",
    "onSubmitEditing",
    "onSlidingStart",
    "onValueChange",
    "onChangeText",
    "onEndEditing",
    "onLongPress",
    "onKeyPress",
    "onPressOut",
    "onPressIn",
    "onChange",
    "onSelect",
    "onToggle",
    "onFocus",
    "onPress",
    "onBlur"
];

export const TOOLTIP_IMAGE_ROLES: Array<string> = [
    "image",
    "img"
];

export const TOOLTIP_LOCATION_PLACEMENTS: Record<TooltipLocation["vertical"], Record<TooltipLocation["horizontal"], TooltipPlacementType>> = {
    bottom: {
        center: {
            align: "center",
            side: "bottom"
        },
        right: {
            align: "start",
            side: "bottom"
        },
        left: {
            side: "bottom",
            align: "end"
        }
    },
    center: {
        center: {
            align: "center",
            side: "top"
        },
        right: {
            align: "center",
            side: "right"
        },
        left: {
            align: "center",
            side: "left"
        }
    },
    top: {
        center: {
            align: "center",
            side: "top"
        },
        right: {
            align: "start",
            side: "top"
        },
        left: {
            align: "end",
            side: "top"
        }
    }
};

export const TOOLTIP_CARET_EDGES: Record<TooltipSide, TooltipSide> = {
    bottom: "top",
    right: "left",
    left: "right",
    top: "bottom"
};

export const TOOLTIP_TITLE_VARIANTS: Record<TooltipMode, keyof NCoreUIKit.Typography> = {
    popover: "titleSmallSize",
    plain: "labelSmallSize"
};

export const TOOLTIP_INVERSE_THEMES: Partial<Record<keyof NCoreUIKit.ThemeKey, keyof NCoreUIKit.ThemeKey>> = {
    light: "dark",
    dark: "light"
};

export const getTooltipPlacement = ({
    location,
    align,
    side
}: TooltipPlacementConstantType): TooltipPlacementType => {
    const locationPlacement = location ? TOOLTIP_LOCATION_PLACEMENTS[location.vertical][location.horizontal] : undefined;

    return {
        align: align ?? locationPlacement?.align ?? "center",
        side: side ?? locationPlacement?.side ?? "top"
    };
};

export const getIsTooltipLocationExact = ({
    location
}: TooltipLocationConstantType): boolean => {
    return (location.horizontal === "center") !== (location.vertical === "center");
};

export const getTooltipIconColor = ({
    colors,
    type
}: TooltipIconColorConstantType): string => {
    return colors.system.onInverse[STATE_COLOR_KEYS[type]];
};

export const getTooltipActionColors = ({
    isPrimary,
    colors
}: TooltipActionColorsConstantType): TooltipActionColorsType => {
    if(isPrimary) {
        return {
            customTextColor: colors.content.container.inverse,
            customColor: colors.system.onInverse.primary
        };
    }

    return {
        customBorderColor: colors.content.text.contrastLow,
        customTextColor: colors.content.text.contrastHigh
    };
};

export const getTooltipInverseTheme = ({
    customTheme,
    activeTheme
}: TooltipInverseThemeConstantType): TooltipCustomThemeType | undefined => {
    const inverseThemeKey = TOOLTIP_INVERSE_THEMES[activeTheme];

    if(!inverseThemeKey) {
        return customTheme;
    }

    return {
        ...customTheme,
        themeKey: inverseThemeKey
    };
};

export const getTooltipCaretOffset = ({
    arrowOffset,
    surfaceSize,
    actualSide,
    caretSize,
    radius
}: TooltipCaretConstantType): number => {
    if(!surfaceSize) {
        return arrowOffset;
    }

    const crossSize = actualSide === "bottom" || actualSide === "top" ? surfaceSize.width : surfaceSize.height;

    const minOffset = radius + caretSize / 2;

    const maxOffset = crossSize - minOffset;

    if(maxOffset < minOffset) {
        return crossSize / 2;
    }

    return Math.min(Math.max(arrowOffset, minOffset), maxOffset);
};

export const getTooltipCaretPosition = ({
    arrowOffset,
    surfaceSize,
    actualSide,
    caretSize,
    radius
}: TooltipCaretConstantType): ViewStyle => {
    const caretOffset = getTooltipCaretOffset({
        arrowOffset,
        surfaceSize,
        actualSide,
        caretSize,
        radius
    });

    const caretPosition: Mutable<ViewStyle> = {};

    caretPosition[TOOLTIP_CARET_EDGES[actualSide]] = -caretSize / 2;

    caretPosition[actualSide === "bottom" || actualSide === "top" ? "left" : "top"] = caretOffset - caretSize / 2;

    return caretPosition;
};

const stylesheet = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFill
    },
    focusableAnchor: {
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    anchor: {
        justifyContent: "center",
        alignSelf: "flex-start",
        position: "relative",
        alignItems: "center"
    },
    content: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    surface: {
        flexDirection: "column",
        position: "absolute",
        left: 0,
        top: 0
    },
    popoverTextContainer: {
        flexDirection: "column",
        flexShrink: 1,
        flexGrow: 1
    },
    textContainer: {
        flexDirection: "column",
        flexShrink: 1
    },
    caret: {
        transform: [
            {
                rotate: "45deg"
            }
        ],
        position: "absolute"
    },
    actions: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    pressableSurface: {
        ...webStyle({
            cursor: "pointer"
        })
    },
    hiddenSurface: {
        pointerEvents: "none"
    },
    passiveAnchor: {
        ...webStyle({
            cursor: "auto"
        })
    }
});

export const useStyles = ({
    isAnchorFocusVisible,
    isAnchorFocusTarget,
    isAnchorPressable,
    controlPaddings,
    isCloseOnPress,
    shapeConfigs,
    arrowOffset,
    pointerType,
    surfaceSize,
    triggerMode,
    windowWidth,
    actualSide,
    typography,
    iconSizes,
    radiuses,
    borders,
    colors,
    height,
    isShow,
    sizing,
    spaces,
    width,
    mode
}: TooltipDynamicStyleType) => {
    const isPopover = mode === "popover";

    const styles = {
        icon: {
            marginTop: Math.max((typography[TOOLTIP_TITLE_VARIANTS[mode]].lineHeight - iconSizes.small) / 2, 0)
        } as Mutable<ViewStyle>,
        surface: {
            maxWidth: Math.min(shapeConfigs.tooltipMaxWidth, windowWidth - spaces.spacingMd * 2),
            paddingHorizontal: isPopover ? spaces.spacingMd : controlPaddings.small,
            paddingVertical: isPopover ? spaces.spacingMd : spaces.spacingSm,
            backgroundColor: colors.content.container.inverse,
            ...getCornerStyle({
                radius: radiuses.form,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        caret: {
            ...getTooltipCaretPosition({
                caretSize: spaces.spacingSm,
                radius: radiuses.form,
                arrowOffset,
                surfaceSize,
                actualSide
            }),
            backgroundColor: colors.content.container.inverse,
            height: spaces.spacingSm,
            width: spaces.spacingSm
        } as Mutable<ViewStyle>,
        closeButton: {
            marginTop: -spaces.spacingXs,
            marginEnd: -spaces.spacingSm
        } as Mutable<ViewStyle>,
        actions: {
            marginTop: spaces.spacingSm,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        textContainer: {
            gap: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        content: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        anchor: {
        } as Mutable<ViewStyle>
    };

    if(sizing === "fixed") {
        delete styles.surface.maxWidth;

        styles.surface.height = height;

        styles.surface.width = width;
    }

    if(!isShow) {
        Object.assign(styles.surface, stylesheet.hiddenSurface);
    }

    if(isCloseOnPress) {
        Object.assign(styles.surface, stylesheet.pressableSurface);
    }

    if(triggerMode === "hover" && pointerType === "fine" && !isAnchorPressable) {
        Object.assign(styles.anchor, stylesheet.passiveAnchor);
    }

    if(isAnchorFocusTarget) {
        Object.assign(styles.anchor, stylesheet.focusableAnchor);

        styles.anchor.borderRadius = radiuses.xs;
    }

    if(isAnchorFocusVisible) {
        Object.assign(styles.anchor, getFocusRingStyle({
            isFocusVisible: isAnchorFocusVisible,
            borders,
            colors
        }));
    }

    return styles;
};
export default stylesheet;
